import { FC, useEffect } from 'react';
import classNames from 'classnames';

import { CurrencySymbolEnum } from '../../../lib/enums/currency.enum';
import lang from '../../../lib/language';

import GoBackButton from '../../../components/GoBackButton/GoBackButton';
import Grid from '../../../components/Grid/Grid';
import NavButtons from '../../../components/NavigationButtons/NavButtons';
import Button from '../../Button/Button';
import { ButtonTypesEnum } from '../../Button/Button.types';
import FilterDropDown from '../../FilterDropDown/FilterDropDown';
import useIsMobile from '../../hooks/useIsMobile';
import NoDataAvailable from '../../NoDataAvailable/NoDataAvailable';
import { EPIC_MOVERS_INTERNAL_TABLE_HEADERS } from '../EpicMovers.constants';

import EpicMoversInternalTab from './components/EpicMoversInternalTab/EpicMoversInternalTab';
import { EpicMoversInternalProps } from './EpicMoversInternal.types';

import styles from './EpicMoversInternal.module.scss';

const EpicMoversInternal: FC<EpicMoversInternalProps> = ({
  rows,
  activeTabIndex,
  isPending,
  isLoading,
  isNoData,
  hasMoreRows,
  filterOptionValue,
  filterCurrencySymbol = CurrencySymbolEnum.USD,
  enableRowToggle,
  isAce,
  titleRef,
  onTabChange,
  onFilterChange,
  onLoadMore,
  onSymbolClick,
  onGoBack,
}: EpicMoversInternalProps): JSX.Element => {
  const { isMobile } = useIsMobile();

  useEffect(() => {
    if (isMobile) {
      window.scrollTo(0, 0);
      document.body.scrollTop = 0; // For Safari
      document.documentElement.scrollTop = 0; // For other browsers
    }
  }, [isMobile]);

  const renderHeaders = () => (isNoData ? <></> : (
    <div className={styles.headersContainer}>
      {EPIC_MOVERS_INTERNAL_TABLE_HEADERS.map(header => (
        <div key={header} className={styles.headersItems}>{header}</div>
      ))}
    </div>
  ));

  // Both tabs render the same page of rows; only the active one is mounted.
  const renderTab = (isGainers: boolean) => (
    <EpicMoversInternalTab
      rows={rows}
      isLoading={isLoading}
      enableRowToggle={enableRowToggle}
      renderHeaders={renderHeaders}
      onSymbolClick={symbol => onSymbolClick(symbol, isGainers)}
    />
  );

  const tabs = [
    { text: lang.commonEpicMoversGainers(), component: renderTab(true) },
    { text: lang.commonEpicMoversLosers(), component: renderTab(false) },
  ];

  return (
    <Grid>
      <div className={classNames(styles.wrapper, { [styles.wrapperAce]: isAce })}>
        {!isMobile && (
          <div className={styles.goBackButton}>
            <GoBackButton onBackBtnPress={onGoBack} />
          </div>
        )}
        <div ref={titleRef} className={styles.title}>
          {lang.commonEpicMoversToday()}
        </div>
        <div className={styles.description}>
          {lang.commonEpicMoversDescription()}
        </div>
        <div className={styles.table}>
          <div className={styles.container}>
            <NavButtons
              tabs={tabs}
              sendCurrentTabIndex={onTabChange}
              initialActiveTab={activeTabIndex}
              inlineFilters={(
                <div className={styles.selector}>
                  <FilterDropDown
                    isDisabled={isPending}
                    optionValue={filterOptionValue}
                    handleFilterOption={onFilterChange}
                    currencySymbol={filterCurrencySymbol as CurrencySymbolEnum}
                  />
                </div>
              )}
            />
            {isNoData && (
              <NoDataAvailable
                className={styles.noDataToDisplay}
                isNoDataIcon
                title={lang.commonDiscover52WeekNoData()}
              />
            )}
            {hasMoreRows && (
              <div className={styles.loadMoreWrapper}>
                <Button
                  className={styles.loadMoreButton}
                  variant={ButtonTypesEnum.Discover}
                  onClick={onLoadMore}
                >
                  {lang.commonLoadMore()}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </Grid>
  );
};

export default EpicMoversInternal;
